import GetTaskListByTreeShortRequest from '@/api/Main/dataSource/api/projects/task/i-service/models/task/request/GetTaskListByTreeShortRequest';
import {
  GetOrderedTasksRequestModel,
  GetOrderedTasksResponseModel,
  GetProjectTasksRequestModel,
  OrderTaskModel,
  PaginationResponseModel,
  ResponseModel,
  TeammateModel,
  UpsertTaskOrderForUserEventResponse,
} from '../models';
import GetTaskListRequestFilter from '@/api/Main/dataSource/api/projects/task/i-service/models/task/response/GetTaskListRequestFilter';
import GetTaskListByTreeShortResponse from '@/api/Main/dataSource/api/projects/task/i-service/models/task/response/GetTaskListByTreeShortResponse';
import {
  TaskModel,
  TaskPersonModel,
  TaskQueueModel,
  TaskStatusModel,
} from '@/components/tasks-kanban/models';
import GetEmployeesDetailForManagerListResponse from '@/api/Main/dataSource/api/projects/performers/i-service/model/response/GetEmployeesDetailForManagerListResponse';
import GetOwnedDetailUsersResponse from '@/api/Main/dataSource/api/projects/performers/i-service/model/response/GetOwnedDetailUsersResponse';
import GetEmployeesDetailForManagerListItem from '@/api/Main/dataSource/api/projects/performers/i-service/model/response/GetEmployeesDetailForManagerListItem';
import GetOwnedDetailUsersItem from '@/api/Main/dataSource/api/projects/performers/i-service/model/response/GetOwnedDetailUsersItem';
import { ProjectModel } from '@/models';
import GetTaskListByTreeShortResponseItem from '@/api/Main/dataSource/api/projects/task/i-service/models/task/response/GetTaskListByTreeShortResponseItem';
import ProjectSearchRequest from '@/api/Main/dataSource/api/projects/base/i-service/models/ProjectSearchRequest';
import EnumProjectType from '@/api/Main/dataSource/api/projects/base/entities/enums/EnumProjectType';
import BaseProjectSearchResponse from '@/api/Main/dataSource/api/projects/base/i-service/models/BaseProjectSearchResponse';
import PageRequest from '@/api/Main/dataSource/api/pagination-helper/lib/PageRequest';
import SubscribeToOrderTaskRequest from '@/api/Main/dataSource/api/projects/task/i-service/models/task-order/request/SubscribeToOrderTaskRequest';
import UnSubscribeToOrderTaskRequest from '@/api/Main/dataSource/api/projects/task/i-service/models/task-order/request/UnSubscribeToOrderTaskRequest';
import DifficultyLevelTaskModel from '@/api/Main/dataSource/api/projects/task/i-service/models/task/response/DifficultyLevelTaskModel';
import SortingSettingsTask from '@/api/Main/dataSource/api/projects/task/i-service/models/task/request/SortingSettingsTask';

export const mapGetTasksByProjectIdRequest = (request: GetProjectTasksRequestModel) =>
  new GetTaskListByTreeShortRequest({
    ProjectId: request.ProjectId,
    NamespaceIds: request.Filter.Namespaces,
    QueueTaskIds: request.Filter.Queues,
    Filter: new GetTaskListRequestFilter({
      Search: request.Filter.Search,
      StatusId: request.Filter.Statuses,
      PriorityId: request.Filter.Priorities,
      DifficultyIds: request.Filter.Difficulties,
      TagIds: request.Filter.Tags,
      EstimateFrom: request.Filter.TimeEstimate?.Start,
      EstimateTo: request.Filter.TimeEstimate?.End,
      WorkTimeFrom: request.Filter.TimeSpent?.Start,
      WorkTimeTo: request.Filter.TimeSpent?.End,
      PerformerIds: request.Filter.Performers,
      ObserverIds: request.Filter.Observers,
      AuthorIds: request.Filter.Authors,
      WithoutPriorities: request.Filter.WithoutPriorities,
      WithoutTags: request.Filter.WithoutTags,
    }),
    Page: request.Page
      ? new PageRequest({
          Take: request.Page.Take,
          Skip: request.Page.Skip,
        })
      : undefined,
    SortingSettings: new SortingSettingsTask({
      OrderDescendingByName: request.Sort.OrderByName,
      OrderDescendingByTaskId: request.Sort.OrderById,
      OrderDescendingByEstimates: request.Sort.OrderByEstimateTime,
      OrderDescendingBySpent: request.Sort.OrderByWorkTime,
    }),
  });

export const mapGetTasksByProjectIdResponse = (response: GetTaskListByTreeShortResponse) =>
  new PaginationResponseModel<Array<OrderTaskModel>>({
    Current: response.Page.Current,
    Count: response.Page.Count,
    Size: response.Page.Size,
    IsSuccess: true,
    Items: response.Items.map(
      (item) =>
        new OrderTaskModel({
          ...mapTaskModel(item, item.ProjectId, true),
          ChangeAccess: item.CanSetUpInOrderTask,
        }),
    ),
  });

/**Получение модели задачи */
export const mapTaskModel = (
  item: GetTaskListByTreeShortResponseItem,
  projectId?: number,
  isTreeShortResponse?: boolean,
) => {
  const task = new TaskModel({
    Id: item.Id,

    ProjectId: projectId,
    EstimateTime: item.EstimateTime,
    WorkTime: item.WorkTime,
    Name: item.Name,
    Difficulty: new DifficultyLevelTaskModel({
      DifficultyId: item?.Difficulty?.DifficultyId,
      DifficultyColor: item?.Difficulty?.DifficultyColor,
      DifficultyName: item?.Difficulty?.DifficultyName,
    }),

    Status: new TaskStatusModel({
      Id: item.Status?.StatusId,
      Name: item.Status?.StatusName,
      Color: item.Status?.StatusColor,
    }),
    Priority: new TaskStatusModel({
      Id: item.Priority?.PriorityId,
      Name: item.Priority?.PriorityName,
      Color: item.Priority?.PriorityColor,
    }),
  });

  if (isTreeShortResponse) {
    item = item as GetTaskListByTreeShortResponseItem;
    Object.assign(task, {
      ProjectId: item.ProjectId || projectId,
      Project: new ProjectModel({
        Id: item.ProjectId || projectId,
        Name: item.ProjectName,
        ImageUrl: item.ProjectImage,
        PreviewColor: item.ProjectPreviewColor,
      }),
      Queues: item.QueueTaskList.map(
        (queue) =>
          new TaskQueueModel({
            Id: queue.Id,
            Name: queue.Name,
            NamespaceId: queue.Namespace.Id,
          }),
      ),
      NamespaceInfo: item.QueueTaskList.map((queue) => queue.Namespace),
      Author: new TaskPersonModel({
        Id: item.Owner.Id,
        FirstName: item.Owner.FirstName,
        LastName: item.Owner.LastName,
        MiddleName: item.Owner.MiddleName,
        ImageUrl: item.Owner.Photo,
        NoPhotoColor: item.Owner.AvatarColor,
      }),
    });
  }
  return task;
};

export const mapGetTeamResponse = (
  response: GetEmployeesDetailForManagerListResponse | GetOwnedDetailUsersResponse,
) => {
  return new ResponseModel<TeammateModel[]>({
    IsSuccess: true,
    Value:
      response?.Items?.map((item) => {
        const user =
          (item as GetEmployeesDetailForManagerListItem).Employee ||
          (item as GetOwnedDetailUsersItem).Profile;
        return new TeammateModel({
          Id: user.UserId,
          FirstName: user.FirstName,
          LastName: user.LastName,
          MiddleName: user.MiddleName,
          Email: user.Email,
          ImageUrl: user.Foto,
          AvatarColor: user.AvatarColor,
          Projects: item.Projects.map(
            (p) =>
              new ProjectModel({
                Id: p.ProjectId,
                Name: p.ProjectName,
                ImageUrl: p.ImageUrl,
                PreviewColor: p.PreviewColor,
              }),
          ),
        });
      }) ?? [],
  });
};

export const mapGetPerformerProjectsRequest = (performerId: number) =>
  new ProjectSearchRequest({
    ShowOnlyPerformerId: performerId,
    Status: [EnumProjectType.Active],
  });

export const mapGetPerformerProjectsResponse = (response: BaseProjectSearchResponse) =>
  response.Projects?.map(
    (project) =>
      new ProjectModel({
        Id: project.Id,
        Name: project.Name,
        ImageUrl: project.ImageUrl,
        PreviewColor: project.PreviewColor,
      }),
  );

export const mapSubscribeOrderListRequest = (userId: number) =>
  new SubscribeToOrderTaskRequest({
    UserId: userId,
  });

export const mapUnSubscribeOrderListRequest = (userId: number) =>
  new UnSubscribeToOrderTaskRequest({
    UserId: userId,
  });

export const mapOnUpsertTaskOrderForUserEventResponse = (
  response: UpsertTaskOrderForUserEventResponse,
) => ({
  Tasks: response.Tasks.map(
    (task) =>
      new TaskModel({
        Id: task.Id,
        Name: task.Name,
        Status: new TaskStatusModel({
          Id: task.Status?.StatusId,
          Name: task.Status?.StatusName,
          Color: task.Status?.StatusColor,
        }),
        Priority: new TaskStatusModel({
          Id: task.Priority?.PriorityId,
          Name: task.Priority?.PriorityName,
          Color: task.Priority?.PriorityColor,
        }),
        EstimateTime: task.EstimateTime,
        WorkTime: task.WorkTime,
      }),
  ),
  UserId: response.UserId,
});
